import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'
import { AttentionSeverityPlate } from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { CockpitRow } from '../../../components/cockpitRow'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import type { IAttentionStatusesItem } from '../../types'
import { AttentionStatusesMeta } from '../../variants/AttentionStatusesMeta'
import { AttentionPartnerLink, useAttentionPartnerPlanUrl } from '../attentionPartnerLink'
import { AttentionPbmReminders } from '../attentionPbmReminders'
import { AttentionReminderButton } from '../attentionReminderButton'

import styles from './AttentionStatusCard.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

/** What a card is painted as: the same three steps the H2 review panel's cards are built on. */
const SEVERITY_PRIORITY: Record<number, 'high' | 'medium' | 'low'> = {
  3: 'high',
  2: 'medium',
  1: 'low',
}

/** One size for every count the list shows: up to three digits the plate holds the figure as it
 *  is, so counts in neighbouring rows are read as one scale. */
const COUNT_FONT_SIZE = '14px'

/** Four digits and over do not fit the circle at that size, and the plate stays a circle. */
const LONG_COUNT_FONT_SIZE = '10px'

interface IAttentionStatusCardProps {
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
  /** Whether the card carries the level's own plate. A board sorts its cards into a column per
   *  level and marks that level in its own head, so there the plate would repeat over every card
   *  under it. */
  showSeverityPlate?: boolean
  /** Whether the worst level is badged over the card's corner. A board already sorts its cards
   *  into a column per level and says that level in its own head, so there the badge would be the
   *  same word twice over every card. */
  showTopBadge?: boolean
  className?: string
}

type TAttentionStatusCardRowProps = Omit<IAttentionStatusCardProps, 'onSelect'> & {
  onPick: () => void
}

const AttentionStatusCardRow = ({
  item,
  onPick,
  showSeverityPlate = true,
  showTopBadge = true,
  className,
}: TAttentionStatusCardRowProps) => {
  const text = useAttentionStatusesText()
  const priority = SEVERITY_PRIORITY[item.severity] ?? 'low'

  return (
    <CockpitRow
      title={item.title}
      description={item.meta?.length ? <AttentionStatusesMeta parts={item.meta} /> : undefined}
      tone="attention"
      className={cn(styles[`card_${priority}`], className)}
      onSelect={onPick}
      badge={
        showTopBadge &&
        priority === 'high' && (
          <span className={styles.topBadge}>
            <span aria-hidden="true">🔥</span> <Translation span text={KEYS.topPriority} />
          </span>
        )
      }
      leading={
        <>
          {showSeverityPlate && item.titleCount !== undefined && (
            <AttentionSeverityPlate severity={item.severity} className={styles.countPlate}>
              <span
                style={{
                  fontSize:
                    String(item.titleCount).length > 3 ? LONG_COUNT_FONT_SIZE : COUNT_FONT_SIZE,
                }}
              >
                {item.titleCount}
              </span>
            </AttentionSeverityPlate>
          )}
          {/* A row with nothing to count carries the level's own word in the plate a counted row
            puts its number in: same shape, same colour, same pulse. */}
          {showSeverityPlate && item.marksSeverity && (
            <AttentionSeverityPlate severity={item.severity} className={styles.markPlate}>
              <Translation span {...text.phrase.severityName(item.severity)} />
            </AttentionSeverityPlate>
          )}
        </>
      }
      trailing={
        <>
          {/* A row that carries a score of its own states it beside the sentence: it is what that
            row is judged by, and the level's colour says nothing about it. */}
          {item.score && (
            <span className={styles.cardScore}>
              <Translation span {...text.phrase.score(item.score.value, item.score.target)} />
            </span>
          )}
          {/* The call closes the card's own line rather than standing under it: the tile holds
            several of these at once, and a line each is what it has room for. */}
          {item.reminder && item.reminderRoute && (
            <AttentionReminderButton
              reminder={item.reminder}
              route={item.reminderRoute}
              list={item.reminderList}
            />
          )}
          {/* A partner row opens nothing inside the tile, so its PBMs are reminded from the row. */}
          {item.pbms?.length ? (
            <AttentionPbmReminders
              pbms={item.pbms}
              route={item.reminderRoute}
              partner={
                item.partnerId ? { partnerId: item.partnerId, partnerName: item.title } : undefined
              }
            />
          ) : null}
          {item.partnerId ? (
            <AttentionPartnerLink partnerId={item.partnerId} partnerName={item.title} />
          ) : item.opensLevel ? (
            <span className={cn(styles.cardAction, styles.cardChevron)} aria-hidden="true">
              <i className="kbase kbase-chevron-right" />
            </span>
          ) : (
            <span className={styles.cardAction}>
              <Translation span {...item.actionLabel} />
            </span>
          )}
        </>
      }
    />
  )
}

/** A partner row that opens no detail inside the tile opens the partner's plan, as the link it
 *  carries does. */
const AttentionPartnerPlanCard = ({
  item,
  ...props
}: Omit<IAttentionStatusCardProps, 'onSelect'>) => {
  const partnerPlanUrl = useAttentionPartnerPlanUrl(item.partnerId ?? '')

  return (
    <AttentionStatusCardRow
      {...props}
      item={item}
      onPick={() => window.open(partnerPlanUrl, '_blank', 'noreferrer')}
    />
  )
}

/**
 * One row of the tile, drawn as the H2 review panel draws its own: a plate in the level's colour
 * carrying what the row counts, the sentence beside it, and the call that closes the line. The
 * worst level is badged over the card's corner and its plate pulses, so what has to be acted on
 * first is found without reading a word.
 *
 * The card is a `div` with a button's role rather than a `button`: a row that stands for a partner
 * carries the way to that partner's own page inside it, and no browser allows a link inside a
 * button. Picking such a row opens that page as well, unless the row opens the partner's detail.
 */
export const AttentionStatusCard = ({ onSelect, ...props }: IAttentionStatusCardProps) =>
  /* A partner row opens something inside the tile only where it opens that partner's detail —
     the PBM's own partner; otherwise the row opens the partner's plan. */
  props.item.partnerId && !props.item.opensDetail ? (
    <AttentionPartnerPlanCard {...props} />
  ) : (
    <AttentionStatusCardRow {...props} onPick={() => onSelect(props.item.id)} />
  )

AttentionStatusCard.displayName = 'AttentionStatusCard'
